import React, { useState, useEffect } from 'react';
import apiService from '../../services/api';
import '../../styles/EmbeddingsManager.css';

function EmbeddingsManager({ onClose }) {
  const [embeddings, setEmbeddings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedEmbedding, setSelectedEmbedding] = useState(null);
  const [stats, setStats] = useState(null);
  const [filters, setFilters] = useState({
    filename: '',
    fileType: ''
  });  const [pagination, setPagination] = useState({
    total: 0,
    limit: 25,
    skip: 0,
    hasMore: true  // Start with true to allow initial pagination
  });
  const [embeddingDetailLoading, setEmbeddingDetailLoading] = useState(false);
  const [filesList, setFilesList] = useState([]);
  const [allFilesList, setAllFilesList] = useState([]); // Store all files for filtering

  // Load embeddings on component mount and when filters or pagination change
  useEffect(() => {
    // Fetch embeddings when component mounts or when filter/pagination changes
    fetchEmbeddings();
    
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters.filename, filters.fileType, pagination.skip, pagination.limit]);
  
  // Load stats and file list on initial mount
  useEffect(() => {
    fetchStats();
    fetchFilesList();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Update files list when file type filter changes (using already fetched data)
  useEffect(() => {
    if (allFilesList.length > 0) {
      // Re-filter the existing files list based on the new file type filter
      let filesToDisplay = allFilesList;
      
      if (filters.fileType) {
        console.log('Re-filtering files by type:', filters.fileType);
        filesToDisplay = allFilesList.filter(file => {
          const lastDotIndex = file.lastIndexOf('.');
          if (lastDotIndex === -1) return false;
          
          // Extract extension with dot (e.g., ".md") to match the format stored in the database
          const ext = file.substring(lastDotIndex).toLowerCase();
          const filterType = filters.fileType.toLowerCase();
          
          // Ensure both have dots for comparison
          const normalizedFilterType = filterType.startsWith('.') ? filterType : '.' + filterType;
          
          return ext === normalizedFilterType;
        });
        console.log(`Re-filtered ${filesToDisplay.length} files of type '${filters.fileType}' from ${allFilesList.length} total files`);
      } else {
        console.log(`Showing all ${allFilesList.length} files`);
      }
      
      setFilesList(filesToDisplay);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters.fileType, allFilesList]);const fetchEmbeddings = async () => {
    setLoading(true);
    setError(null); // Clear any previous errors
    try {
      console.log(`Fetching embeddings with skip=${pagination.skip}, limit=${pagination.limit}, filename=${filters.filename}, fileType=${filters.fileType}`);
      
      const data = await apiService.getEmbeddings(
        filters.filename || null,
        pagination.limit,
        pagination.skip,
        filters.fileType || null
      );
      
      console.log('Received embeddings data:', {
        count: data.embeddings.length,
        pagination: data.pagination
      });
      
      setEmbeddings(data.embeddings);
      
      // Make sure to update pagination with the server response
      setPagination({
        total: data.pagination.total,
        limit: pagination.limit,
        skip: pagination.skip,
        hasMore: (pagination.skip + pagination.limit) < data.pagination.total
      });
      
      // If we get no embeddings but expected some, show a helpful message
      if (data.embeddings.length === 0 && pagination.skip > 0) {
        // We might have requested a page that doesn't exist anymore
        console.warn('Requested page has no embeddings, resetting to first page');
        setPagination({
          ...pagination,
          skip: 0,
          hasMore: data.pagination.total > pagination.limit
        });
        // Re-fetch with skip=0
        setTimeout(() => fetchEmbeddings(), 0);
      }
    } catch (err) {
      setError('Failed to load embeddings. Please try again.');
      console.error('Error fetching embeddings:', err);
      
      // Reset to first page if we encounter an error during pagination
      if (pagination.skip > 0) {
        setPagination({
          ...pagination,
          skip: 0,
          hasMore: true
        });
      }
    } finally {
      setLoading(false);
    }
  };

  const fetchFilesList = async () => {
    try {
      const response = await apiService.getFilesList();
      const allFiles = response.documents || [];
      
      // Store all files for future filtering
      setAllFilesList(allFiles);
      
      // Filter files based on current file type filter
      let filesToDisplay = allFiles;
      
      if (filters.fileType) {
        console.log('Filtering files by type:', filters.fileType);
        filesToDisplay = allFiles.filter(file => {
          // Extract extension from filename (everything after the last dot)
          const lastDotIndex = file.lastIndexOf('.');
          if (lastDotIndex === -1) return false; // No extension
          
          // Extract extension with dot (e.g., ".md") to match the format stored in the database
          const ext = file.substring(lastDotIndex).toLowerCase();
          const filterType = filters.fileType.toLowerCase();
          
          // Ensure both have dots for comparison
          const normalizedFilterType = filterType.startsWith('.') ? filterType : '.' + filterType;
          
          return ext === normalizedFilterType;
        });
        console.log(`Filtered ${filesToDisplay.length} files of type '${filters.fileType}' from ${allFiles.length} total files`);
        console.log('Filtered files:', filesToDisplay);
      } else {
        console.log(`Showing all ${allFiles.length} files`);
      }
      
      setFilesList(filesToDisplay);
    } catch (err) {
      console.error('Error fetching files list:', err);
    }
  };

  const fetchStats = async () => {
    try {
      const data = await apiService.getEmbeddingsStats();
      setStats(data);
    } catch (err) {
      console.error('Error fetching embeddings stats:', err);
    }
  };

  const fetchEmbeddingDetail = async (embeddingId) => {
    setEmbeddingDetailLoading(true);
    try {
      const data = await apiService.getEmbeddingDetail(embeddingId);
      setSelectedEmbedding(data);
    } catch (err) {
      setError('Failed to load embedding details. Please try again.');
      console.error('Error fetching embedding detail:', err);
    } finally {
      setEmbeddingDetailLoading(false);
    }
  };
  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    
    // If changing file type, clear filename filter as it may not be valid for new type
    if (name === 'fileType') {
      setFilters({ filename: '', fileType: value });
    } else {
      setFilters({ ...filters, [name]: value });
    }
    
    // Reset pagination when filter changes
    setPagination({ 
      ...pagination, 
      skip: 0,
      // Reset hasMore to true to allow pagination, will be updated after fetch
      hasMore: true 
    });
    // Clear any selected embedding when changing filters
    setSelectedEmbedding(null);
  };const handleNextPage = () => {
    if (pagination.hasMore && !loading) {
      // Set loading state to provide visual feedback
      setLoading(true);
      // Update skip value and trigger a fetch with the new pagination
      const newSkip = pagination.skip + pagination.limit;
      setPagination({ 
        ...pagination, 
        skip: newSkip,
        // Update hasMore based on what we know about total
        hasMore: (newSkip + pagination.limit) < pagination.total
      });
      // The useEffect will trigger fetchEmbeddings with the new skip value
      setSelectedEmbedding(null); // Clear selected embedding when changing page
    }
  };

  const handlePrevPage = () => {
    if (pagination.skip > 0 && !loading) {
      // Set loading state to provide visual feedback
      setLoading(true);
      // Calculate new skip value
      const newSkip = Math.max(0, pagination.skip - pagination.limit);
      setPagination({ 
        ...pagination, 
        skip: newSkip,
        // When going back, we always have more data if we're not at the beginning and not on the first page
        hasMore: newSkip > 0 || (pagination.skip + pagination.limit) < pagination.total 
      });
      // The useEffect will trigger fetchEmbeddings with the new skip value
      setSelectedEmbedding(null); // Clear selected embedding when changing page
    }
  };

  const handleSelectEmbedding = (embeddingId) => {
    if (selectedEmbedding && selectedEmbedding.id === embeddingId) {
      // If clicking the already selected embedding, deselect it
      setSelectedEmbedding(null);
    } else {
      fetchEmbeddingDetail(embeddingId);
    }
  };

  const handleDeleteFile = async (filename) => {
    if (window.confirm(`Are you sure you want to delete all embeddings for "${filename}"?`)) {
      try {
        await apiService.deleteDocument(filename);
        // Refresh data
        fetchEmbeddings();
        fetchStats();
        fetchFilesList();
        setSelectedEmbedding(null);
      } catch (err) {
        setError('Failed to delete file. Please try again.');
        console.error('Error deleting file:', err);
      }
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'Unknown';
    try {
      const date = new Date(dateString);
      return date.toLocaleString();
    } catch (err) {
      return dateString;
    }
  };

  return (
    <div className="embeddings-manager">
      {/* <div className="embeddings-header">
        <h2>Document Embeddings Manager</h2>
        <button className="close-button" onClick={onClose}>
          <span className="material-icons">close</span>
        </button>
      </div> */}

      <div className="embeddings-stats">
        {stats && (
          <div className="stats-cards">
            <div className="stat-card">
              <h3>Total Embeddings</h3>
              <div className="stat-value">{stats.total_embeddings}</div>
            </div>
            <div className="stat-card">
              <h3>Unique Files</h3>
              <div className="stat-value">{stats.unique_files}</div>
            </div>
            <div className="stat-card">
              <h3>Last Updated</h3>
              <div className="stat-value">{formatDate(stats.latest_update)}</div>
            </div>
            <div className="stat-card">
              <h3>File Types</h3>
              <div className="file-type-list">
                {Object.entries(stats.file_type_counts || {}).map(([type, count]) => (
                  <div key={type} className="file-type-item">
                    <span className="file-type">{type || 'unknown'}</span>
                    <span className="file-count">{count}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}
      </div>      <div className="embeddings-filters">
        <div className="filter-item">
          <label htmlFor="filename-filter">Filter by File:</label>
          <select
            id="filename-filter"
            name="filename"
            value={filters.filename}
            onChange={handleFilterChange}
          >
            <option value="">All Files</option>
            {filesList.map((file) => (
              <option key={file} value={file}>
                {file}
              </option>
            ))}
          </select>
        </div>
        
        <div className="filter-item">
          <label htmlFor="filetype-filter">Filter by Type:</label>
          <select
            id="filetype-filter"
            name="fileType"
            value={filters.fileType}
            onChange={handleFilterChange}
          >
            <option value="">All Types</option>
            {stats && Object.keys(stats.file_type_counts || {}).sort().map((type) => (
              <option key={type} value={type}>
                {type || 'unknown'} ({stats.file_type_counts[type]})
              </option>
            ))}
          </select>
        </div>

        <div className="filter-actions">
          <button 
            className="refresh-btn" 
            onClick={() => {
              fetchEmbeddings();
              fetchStats();
              fetchFilesList();
            }}
            title="Refresh embeddings data"
            disabled={loading}
          >
            <span className={`material-icons ${loading ? 'spin' : ''}`}>refresh</span>
            Refresh
          </button>

          {filters.filename && (
            <button 
              className="delete-file-btn" 
              onClick={() => handleDeleteFile(filters.filename)}
            >
              <span className="material-icons">delete</span>
              Delete File
            </button>
          )}
        </div>
      </div>

      <div className="embeddings-container">
        <div className="embeddings-list">
          <h3>Embeddings {pagination.total > 0 && `(${pagination.total} total)`}</h3>
            { error ? (
            <div className="error-message">
              <span className="material-icons error-icon">error_outline</span>
              <p>{error}</p>
              <button 
                className="retry-button"
                onClick={() => {
                  // Reset pagination to first page and retry
                  setPagination({...pagination, skip: 0, hasMore: true});
                  fetchEmbeddings();
                }}
              >
                <span className="material-icons">refresh</span>
                Retry
              </button>
            </div>
          ) : embeddings.length === 0 ? (
            <div className="no-embeddings">
              <span className="material-icons">search_off</span>
              <p>No embeddings found. Try adjusting your filters.</p>
            </div>
          ) : (
            <div className="embeddings-table-container">
              <table className="embeddings-table">
                <thead>
                  <tr>
                    <th>ID</th>
                    <th>File</th>
                    <th>Page</th>
                    <th>Chunk</th>
                    <th>Size</th>
                    <th>Date</th>
                  </tr>
                </thead>
                <tbody>
                  {embeddings.map((embedding) => (
                    <tr
                      key={embedding.id}
                      className={selectedEmbedding && selectedEmbedding.id === embedding.id ? 'selected' : ''}
                      onClick={() => handleSelectEmbedding(embedding.id)}
                    >
                      <td title={embedding.id}>{embedding.id.substring(0, 8)}...</td>
                      <td title={embedding.filename}>{embedding.filename}</td>
                      <td>{embedding.page}</td>
                      <td>{embedding.chunk_index}/{embedding.total_chunks || '?'}</td>
                      <td>{embedding.content_length} chars</td>
                      <td title={embedding.timestamp}>{formatDate(embedding.timestamp)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}          <div className="pagination">
            <button 
              onClick={handlePrevPage} 
              disabled={pagination.skip === 0 || loading}
              className={pagination.skip === 0 || loading ? "disabled" : ""}
              title={pagination.skip === 0 ? "Already on first page" : loading ? "Loading..." : "Go to previous page"}
            >
              {loading && pagination.skip > 0 ? (
                <span className="material-icons spin">sync</span>
              ) : (
                <span className="material-icons">chevron_left</span>
              )}
              Previous
            </button>
            <span>
              {loading ? (
                "Loading..."
              ) : pagination.total > 0 ? (
                `${pagination.skip + 1}-${Math.min(pagination.skip + pagination.limit, pagination.total)} of ${pagination.total}`
              ) : (
                "0 results"
              )}
            </span>
            <div className="page-size-selector">
              <label htmlFor="page-size">Items per page:</label>
              <select 
                id="page-size" 
                value={pagination.limit}
                onChange={(e) => {
                  const newLimit = Number(e.target.value);
                  setPagination({
                    ...pagination,
                    limit: newLimit,
                    skip: 0, // Reset to first page when changing page size
                    hasMore: pagination.total > newLimit
                  });
                }}
                disabled={loading}
              >
                <option value="10">10</option>
                <option value="25">25</option>
                <option value="50">50</option>
                <option value="100">100</option>
              </select>
            </div>
            <button 
              onClick={handleNextPage} 
              disabled={!pagination.hasMore || loading}
              className={!pagination.hasMore || loading ? "disabled" : ""}
              title={!pagination.hasMore ? "No more pages" : loading ? "Loading..." : "Go to next page"}
            >
              Next
              {loading && pagination.hasMore ? (
                <span className="material-icons spin">sync</span>
              ) : (
                <span className="material-icons">chevron_right</span>
              )}
            </button>
          </div>
        </div>

        <div className="embedding-detail">
          <h3>Embedding Content Preview</h3>
          { !selectedEmbedding ? (
            <div className="no-embedding-selected">
              <p>Select an embedding from the list to view its content.</p>
            </div>
          ) : (
            <div className="embedding-content">
              <div className="embedding-metadata">
                <div className="metadata-item">
                  <strong>Filename:</strong> {selectedEmbedding.filename}
                </div>
                <div className="metadata-item">
                  <strong>Source:</strong> {selectedEmbedding.source}
                </div>
                <div className="metadata-item">
                  <strong>Page:</strong> {selectedEmbedding.page}
                </div>
                <div className="metadata-item">
                  <strong>Chunk:</strong> {selectedEmbedding.chunk_index}/{selectedEmbedding.total_chunks || '?'}
                </div>
                <div className="metadata-item">
                  <strong>Date:</strong> {formatDate(selectedEmbedding.timestamp)}
                </div>
              </div>
              <div className="embedding-content-text">
                <h4>Content:</h4>
                <pre>{selectedEmbedding.content}</pre>
              </div>
              <div className="embedding-full-metadata">
                <h4>Full Metadata:</h4>
                <pre>{JSON.stringify(selectedEmbedding.metadata, null, 2)}</pre>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default EmbeddingsManager;